// Toast/snackbar mínimo (ver informe "LYNX — Fase 1.1: Usuarios y
// Seguridad", ajuste "No usar alert()"). No existía ningún sistema de
// notificaciones de este tipo en el proyecto — lo que sí existe
// (NotificationBell / useWebPushNotifications) es push notifications del
// navegador, un concepto completamente distinto. Este es nuevo, chico y
// autocontenido: sin Context/Provider global a propósito (hoy solo lo usa
// Users.tsx) — si otro módulo lo necesita más adelante, se promueve.
import React, { useEffect } from "react";

export type ToastVariant = "success" | "warning" | "error";

// Línea con check/cruz — para el caso "guardé A y B pero C falló", donde el
// usuario tiene que ver explícitamente qué de todo lo que tocó se guardó y
// qué no.
export type ToastLine = { label: string; ok: boolean };

export type ToastMessage = {
  variant: ToastVariant;
  title: string;
  lines?: ToastLine[];
  // Texto libre adicional (ej. "Motivo: Debe contener al menos una mayúscula.").
  detail?: string;
};

const ICON_BY_VARIANT: Record<ToastVariant, string> = {
  success: "🟢",
  warning: "⚠️",
  error: "🔴",
};

const BORDER_BY_VARIANT: Record<ToastVariant, string> = {
  success: "var(--success, #1a7f37)",
  warning: "#c99a1f",
  error: "var(--danger, #d1242f)",
};

// Éxito se autodescarta (no hay nada más que leer); warning/error se quedan
// hasta que el usuario los cierra a propósito — llevan un motivo que vale
// la pena poder releer con calma.
const AUTO_DISMISS_MS = 4500;

export default function Toast({ message, onClose }: { message: ToastMessage | null; onClose: () => void }) {
  useEffect(() => {
    if (!message || message.variant !== "success") return;
    const timer = setTimeout(onClose, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        top: "24px",
        right: "24px",
        zIndex: 200,
        maxWidth: "380px",
        background: "var(--surface-alt, #1c1c1c)",
        border: `1px solid ${BORDER_BY_VARIANT[message.variant]}`,
        borderRadius: "14px",
        padding: "14px 16px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <span style={{ fontSize: "16px", lineHeight: "20px" }}>{ICON_BY_VARIANT[message.variant]}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--text-primary)" }}>{message.title}</div>

          {message.lines && message.lines.length > 0 && (
            <ul style={{ margin: "8px 0 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "4px" }}>
              {message.lines.map((line, i) => (
                <li key={i} style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                  {line.ok ? "✔" : "❌"} {line.label}
                </li>
              ))}
            </ul>
          )}

          {message.detail && (
            <div style={{ marginTop: "8px", fontSize: "13px", color: "var(--text-secondary)" }}>{message.detail}</div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar notificación"
          style={{ background: "transparent", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "16px", lineHeight: 1, padding: 0 }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
